import { useLazyQuery } from '@apollo/client';
import { useCallback, useEffect, useRef } from 'react';
import { GENERATE_REBALANCE_INSIGHT_NARRATIVE } from '../queries/insightNarrative.queries';
import type { GenerateRebalanceInsightNarrativeInput } from '../types/insightNarrative.types';
import { buildRebalanceInsightNarrativeInputKey } from '../utils/insights/buildRebalanceInsightNarrativeInputKey';

type GenerateRebalanceInsightNarrativeData = {
  generateRebalanceInsightNarrative?: {
    response?: string | null;
  } | null;
};

export const useRebalanceInsightNarrative = (
  input: GenerateRebalanceInsightNarrativeInput,
) => {
  const inputKey = buildRebalanceInsightNarrativeInputKey(input);
  const generatedForKeyRef = useRef<string | null>(null);
  const [runQuery, { data, loading, error, called }] = useLazyQuery<
    GenerateRebalanceInsightNarrativeData,
    { input: GenerateRebalanceInsightNarrativeInput }
  >(GENERATE_REBALANCE_INSIGHT_NARRATIVE, {
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    generatedForKeyRef.current = null;
  }, [inputKey]);

  const generate = useCallback(
    (nextInput: GenerateRebalanceInsightNarrativeInput) => {
      generatedForKeyRef.current =
        buildRebalanceInsightNarrativeInputKey(nextInput);
      return runQuery({ variables: { input: nextInput } });
    },
    [runQuery],
  );

  const isCurrent = generatedForKeyRef.current === inputKey;

  return {
    generate,
    narrative: isCurrent
      ? (data?.generateRebalanceInsightNarrative?.response ?? null)
      : null,
    loading: isCurrent && loading,
    error: isCurrent ? error : undefined,
    called: isCurrent && called,
  };
};
